"use client";

import { useAuthStore } from "@/stores/authStore";
import { Permission } from "@/types/roles";
import type { User } from "@/types/auth";
import { useMemo, useCallback } from "react";

/**
 * Same rule as the hook, callable outside React — the login handler needs it
 * before any component has re-rendered with the new session.
 */
export function userHasPermission(
  user: User | null | undefined,
  permission: Permission,
): boolean {
  if (!user) return false;
  if (user.isSystemAdmin || user.role === "SYSTEM_ADMIN") return true;
  if (user.role === "COMPANY_OWNER") return true;
  const granted = user.permissions?.length
    ? user.permissions
    : (user.customRole?.permissions.map((p) => p.permission) ?? []);
  return granted.includes(permission);
}

/**
 * Hook for checking user permissions
 * Based on CUSTOM_ROLE_SYSTEM.md
 */
export function usePermissions() {
  const user = useAuthStore((state) => state.user);

  // getMe/login return a flat `permissions` array and no customRole object, so
  // that is the authoritative source; the relation is only a fallback.
  const userPermissions = useMemo(
    () =>
      user?.permissions?.length
        ? user.permissions
        : (user?.customRole?.permissions.map((p) => p.permission) ?? []),
    [user],
  );

  const hasPermission = useCallback(
    (permission: Permission): boolean => userHasPermission(user, permission),
    [user],
  );

  const hasAnyPermission = useCallback(
    (permissions: Permission[]): boolean => {
      return permissions.some((p) => hasPermission(p));
    },
    [hasPermission],
  );

  const hasAllPermissions = useCallback(
    (permissions: Permission[]): boolean => {
      return permissions.every((p) => hasPermission(p));
    },
    [hasPermission],
  );

  const isSystemAdmin = useMemo(() => {
    return user?.isSystemAdmin || user?.role === "SYSTEM_ADMIN";
  }, [user]);

  const isCompanyOwner = useMemo(() => {
    return user?.role === "COMPANY_OWNER";
  }, [user]);

  return {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    isSystemAdmin,
    isCompanyOwner,
    userPermissions,
  };
}
